import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Users, Heart, Radio, Share2, MessageSquare, Calendar, Award, Camera, Pencil } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function CreatorPage() {
  const { username } = useParams();
  const { user, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ display_name: "", bio: "" });
  const avatarInput = useRef(null);
  const bannerInput = useRef(null);

  const load = () => { api.get(`/users/${username}`).then((r) => setProfile(r.data)).catch(() => setProfile(false)); };
  useEffect(load, [username]);

  const toggleFollow = async () => {
    if (!profile) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/users/${profile.id}/follow`);
      toast.success(data.following ? `Seguindo @${profile.username}` : `Deixou de seguir @${profile.username}`);
      load();
    } catch { toast.error("Falha ao atualizar seguidor"); }
    finally { setLoading(false); }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    try {
      const body = new FormData();
      body.append("image", file);
      const { data } = await api.post(`/users/me/media/${kind}`, body);
      setUser(data);
      load();
      toast.success(kind === "avatar" ? "Foto atualizada" : "Banner atualizado");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Não foi possível enviar a imagem");
    }
  };

  const openEditor = () => {
    setEditForm({ display_name: profile.display_name || "", bio: profile.bio || "" });
    setEditing(true);
  };

  const saveProfile = async () => {
    try {
      const { data } = await api.patch("/users/me", editForm);
      setUser(data);
      setProfile((current) => ({ ...current, ...data }));
      setEditing(false);
      toast.success("Perfil atualizado");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Não foi possível atualizar o perfil");
    }
  };

  if (profile === null) return <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm font-mono">carregando…</div>;
  if (profile === false) return <div className="flex-1 flex items-center justify-center text-muted-foreground">Criador não encontrado.</div>;

  const isSelf = user?.id === profile.id;
  const bannerUrl = profile.banner_url || "https://images.unsplash.com/photo-1672872476232-da16b45c9001?crop=entropy&cs=srgb&fm=jpg&w=1600&q=70";

  return (
    <main className="flex-1 min-w-0 overflow-y-auto scrollbar-thin" data-testid="creator-page">
      {/* Banner */}
      <div className={`relative h-56 sm:h-72 overflow-hidden border-b border-[hsl(var(--nx-border))] ${isSelf ? "group cursor-pointer" : ""}`} onClick={() => isSelf && bannerInput.current?.click()}>
        <img src={bannerUrl} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        <div className="absolute inset-0 nx-scanline opacity-30" />
        {isSelf && <div className="absolute inset-0 grid place-items-center bg-black/35 opacity-0 group-hover:opacity-100 transition-opacity"><span className="flex items-center gap-2 font-semibold"><Camera className="w-5 h-5" /> Trocar banner</span></div>}
        {isSelf && <input ref={bannerInput} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => upload("banner", e.target.files?.[0])} />}
      </div>

      {/* Header */}
      <div className="px-6 lg:px-10 -mt-20 relative">
        <div className="flex flex-col sm:flex-row sm:items-end gap-6">
          <button type="button" disabled={!isSelf} onClick={() => avatarInput.current?.click()} className={`relative rounded-full group ${isSelf ? "cursor-pointer" : "cursor-default"}`}>
            <Avatar className="w-32 h-32 ring-4 ring-background nx-glow-blue">
              <AvatarImage src={profile.avatar_url} />
              <AvatarFallback className="text-3xl bg-gradient-to-br from-[hsl(var(--nx-blue))] to-[hsl(var(--nx-cyan))] text-white">{profile.username[0].toUpperCase()}</AvatarFallback>
            </Avatar>
            {isSelf && <span className="absolute inset-0 rounded-full grid place-items-center bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity"><Camera className="w-6 h-6" /></span>}
            {isSelf && <input ref={avatarInput} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => upload("avatar", e.target.files?.[0])} />}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="font-display font-bold text-3xl lg:text-4xl">{profile.display_name || profile.username}</h1>
              <span className="nx-chip nx-chip-cyan !py-0.5">✓ verificado</span>
            </div>
            <div className="text-sm text-muted-foreground">@{profile.username}</div>
            <div className="flex gap-4 mt-2 text-sm">
              <div><span className="font-bold text-foreground">{profile.followers}</span> <span className="text-muted-foreground">seguidores</span></div>
              <div><span className="font-bold text-foreground">{profile.following}</span> <span className="text-muted-foreground">seguindo</span></div>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            {isSelf && <Button onClick={openEditor} className="rounded-xl nx-btn-blue border-0 h-10 px-5"><Pencil className="w-4 h-4 mr-2" /> Editar perfil</Button>}
            {!isSelf && (
              <Button onClick={toggleFollow} disabled={loading} data-testid="creator-follow-btn"
                className={`rounded-xl h-10 px-5 font-semibold ${profile.is_following ? "bg-[hsl(var(--nx-panel-3))] text-foreground hover:bg-[hsl(var(--nx-panel-2))]" : "nx-btn-blue border-0"}`}>
                <Heart className="w-4 h-4 mr-2" /> {profile.is_following ? "Seguindo" : "Seguir"}
              </Button>
            )}
            <Button variant="outline" className="rounded-xl border-[hsl(var(--nx-border))] h-10 hover:bg-white/5" data-testid="creator-message-btn">
              <MessageSquare className="w-4 h-4 mr-2" /> Mensagem
            </Button>
            <Button variant="outline" size="icon" className="rounded-xl border-[hsl(var(--nx-border))] h-10 w-10 hover:bg-white/5">
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {profile.bio && <p className="mt-6 max-w-2xl text-muted-foreground">{profile.bio}</p>}
        {isSelf && !profile.bio && <button onClick={openEditor} className="mt-6 text-sm text-muted-foreground hover:text-foreground">+ Adicionar biografia</button>}
      </div>

      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="nx-panel border-[hsl(var(--nx-border))]">
          <DialogHeader><DialogTitle>Editar perfil</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Nome exibido</Label><Input value={editForm.display_name} maxLength={80} onChange={(e) => setEditForm((form) => ({ ...form, display_name: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Biografia</Label><Textarea value={editForm.bio} maxLength={280} rows={5} onChange={(e) => setEditForm((form) => ({ ...form, bio: e.target.value }))} /><div className="text-xs text-muted-foreground text-right">{editForm.bio.length}/280</div></div>
            <Button onClick={saveProfile} className="w-full nx-btn-blue border-0">Salvar alterações</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tabs */}
      <div className="px-6 lg:px-10 mt-10 pb-16">
        <Tabs defaultValue="sobre" data-testid="creator-tabs">
          <TabsList className="nx-panel-2 p-1 h-auto rounded-full">
            {[
              { v: "sobre", l: "Sobre" },
              { v: "agenda", l: "Agenda" },
              { v: "videos", l: "Vídeos" },
              { v: "clipes", l: "Clipes" },
            ].map((t) => (
              <TabsTrigger key={t.v} value={t.v} data-testid={`tab-${t.v}`}
                className="rounded-full px-4 py-1.5 data-[state=active]:nx-btn-blue data-[state=active]:text-white text-muted-foreground data-[state=active]:font-semibold text-xs uppercase tracking-widest">
                {t.l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="sobre" className="pt-6">
            <div className="grid sm:grid-cols-3 gap-4 max-w-3xl">
              {[
                { icon: Users, label: "Seguidores", val: profile.followers, c: "hsl(var(--nx-blue))" },
                { icon: Radio, label: "Lives feitas", val: 0, c: "hsl(var(--nx-magenta))" },
                { icon: Award, label: "Assinantes", val: 0, c: "hsl(var(--nx-cyan))" },
              ].map((s) => (
                <div key={s.label} className="nx-panel p-5">
                  <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ background: `${s.c}22`, color: s.c }}>
                    <s.icon className="w-5 h-5" />
                  </div>
                  <div className="text-2xl font-display font-bold mt-3">{s.val}</div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">{s.label}</div>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="agenda" className="pt-6">
            <div className="nx-panel p-8 text-center">
              <Calendar className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">Nenhuma transmissão agendada.</p>
            </div>
          </TabsContent>
          <TabsContent value="videos" className="pt-6">
            <div className="nx-panel p-8 text-center text-sm text-muted-foreground">Nenhum vídeo publicado ainda.</div>
          </TabsContent>
          <TabsContent value="clipes" className="pt-6">
            <div className="nx-panel p-8 text-center text-sm text-muted-foreground">Nenhum clipe criado.</div>
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
